import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { defaultOgImageMeta } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { EnquiryForm } from "@/components/EnquiryForm";
import { ExpandingServiceCards } from "@/components/ExpandingServiceCards";
import { ProcessPath } from "@/components/ProcessPath";

const HeroContour = lazy(() =>
  import("@/components/HeroContour").then((module) => ({ default: module.HeroContour })),
);

const HERO_SRCSET = [640, 1024, 1600, 1664]
  .map((w) => `/images/hero/hero-textile-containers-${w}.webp ${w}w`)
  .join(", ");

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title: "UK Textile Recycling & Secure Disposal | Preserve Earth",
      },
      {
        name: "description",
        content:
          "Textile recycling, confidential waste disposal, IT & media recycling and product destruction for UK firms, with an electronic note of secure destruction.",
      },
      {
        property: "og:title",
        content: "UK Textile Recycling & Secure Disposal | Preserve Earth",
      },
      {
        property: "og:description",
        content:
          "Textile recycling, confidential waste disposal, IT & media recycling and product destruction for UK firms, with an electronic note of secure destruction.",
      },
      { name: "twitter:title", content: "UK Textile Recycling & Secure Disposal | Preserve Earth" },
      { name: "twitter:description", content: "Textile recycling, confidential waste disposal, IT & media recycling and product destruction for UK firms, with an electronic note of secure destruction." },
      { property: "og:url", content: "https://preserveearth.co.uk/" },
      { property: "og:type", content: "website" },
      ...defaultOgImageMeta,
    ],
    links: [{ rel: "canonical", href: "https://preserveearth.co.uk/" }],
  }),
  component: HomePage,
});

const services = [
  {
    n: "01",
    slug: "textile-recycling",
    title: "Textile Recycling",
    body: "Branded uniforms, workwear and end-of-life textiles diverted from landfill through careful sorting and recovery, with every collection logged and accounted for.",
    link: "View the process",
  },
  {
    n: "02",
    slug: "confidential-waste-disposal",
    title: "Confidential Waste Disposal",
    body: "GDPR-compliant paper shredding. We collect your documents, transport them securely and shred them off-site at a secure facility, with an electronic note of secure destruction.",
    link: "Data security protocols",
  },
  {
    n: "03",
    slug: "it-and-media-recycling",
    title: "IT & Media Recycling",
    body: "Secure decommissioning of hard drives, redundant hardware and digital media, with full chain of custody and destruction documentation.",
    link: "Media handling guide",
  },
  {
    n: "04",
    slug: "product-destruction",
    title: "Secure Product Destruction",
    body: "Protect brand integrity with secure, documented destruction of unsold inventory, recalled stock and branded materials. Sustainable downstream routing as standard.",
    link: "Brand protection",
  },
  {
    n: "05",
    slug: "one-off-shredding",
    title: "One-off Shredding",
    body: "Single-collection shredding for office moves, archive clear-outs and end-of-quarter compliance. Same accountability, no contract required.",
    link: "Request a collection",
  },
];

// Wording matches the collection steps on the About page. Recovery is omitted
// here because that sentence refers to tiers set out on that page.
const collectionSteps = [
  {
    title: "Collection",
    body: "Discreet, secure collection in unmarked, GPS-tracked vehicles with CCTV.",
  },
  {
    title: "Verified handling",
    body: "Handled by fully vetted staff, with a full chain of custody from collection to outcome.",
  },
  {
    title: "Secure shredding",
    body: "Items are shredded beyond recognition at secure facilities.",
  },
  {
    title: "Audit record",
    body: "Every job gets an electronic note of secure destruction. Clients can opt for an Environmental Impact Statement showing the CO2e savings.",
  },
];

const homeGuides = [
  {
    slug: "ico-confidential-waste-disposal-guide",
    title: "What does the ICO expect when you dispose of confidential waste?",
    blurb:
      "Locked bins, cross-cut shredding, certificates of destruction and disposal logs, explained in plain English.",
  },
  {
    slug: "eco-friendly-ppe-disposal-for-businesses",
    title: "Eco-friendly PPE disposal for businesses",
    blurb: "How to retire branded PPE and workwear securely, with a clear record of what happened to it.",
  },
  {
    slug: "corporate-uniform-recycling-a-sustainable-solution-for-business-waste",
    title: "Why shred retired corporate uniforms?",
    blurb: "Why branded uniforms need secure destruction before recycling, and how a programme is set up.",
  },
];

function HomePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="#enquire"
      />

      <main>
        {/* Hero */}
        <Hero />

        {/* Credibility strip */}
        <section aria-label="Compliance and credentials" className="border-b border-charcoal/10 bg-cream">
          <div className="container-page py-8">
            <ul className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm text-charcoal/75 lg:grid-cols-4">
              {[
                "GDPR-compliant shredding",
                "Electronic note of secure destruction",
                "Sealed, secure collections",
                "Full audit trail",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 leading-snug">
                  <CheckIcon />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Responsibility narrative */}
        <Reveal>
          <section id="about" className="container-page py-32">
            <div className="grid gap-16 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
                  Our environmental responsibility
                </p>
                <h2 className="font-serif text-4xl leading-tight text-charcoal">
                  Sustainability should never come at the cost of quality.
                </h2>
              </div>
              <div className="space-y-6 text-lg leading-relaxed text-charcoal/75 lg:col-span-7 lg:col-start-6">
                <p>
                  Every service offered is engineered to meet the highest environmental standards, so your business can
                  operate sustainably without compromising brand integrity.
                </p>
                <p>
                  As environmental awareness grows, the demand for responsible business solutions is rising. Preserve
                  Earth gives UK businesses practical, effective ways to reduce environmental impact, backed by clear
                  recycling reporting.
                </p>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Services */}
        <section
          id="services"
          aria-labelledby="services-heading"
          className="container-page scroll-mt-24 pb-32"
        >
          <Reveal>
            <div className="mb-16 flex items-end justify-between gap-8">
              <div>
                <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">Services</p>
                <h2 id="services-heading" className="font-serif text-4xl text-charcoal">
                  Compliant disposal pathways.
                </h2>
                <div className="mt-6 h-px w-24 bg-moss" />
              </div>
            </div>
          </Reveal>

          <ExpandingServiceCards services={services} />
        </section>

        <section
          id="how-a-collection-works"
          aria-labelledby="how-heading"
          className="container-page scroll-mt-24 pb-32"
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">Process</p>
          <h2 id="how-heading" className="max-w-[14ch] font-serif text-4xl text-charcoal">
            How a collection works.
          </h2>
          <div className="mt-6 h-px w-24 bg-moss" />
          <ProcessPath steps={collectionSteps} />
        </section>

        {/* Guides */}
        <section aria-labelledby="home-guides" className="container-page pb-32">
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">Guides</p>
          <h2 id="home-guides" className="font-serif text-3xl text-charcoal md:text-4xl">
            Practical guidance for compliance teams.
          </h2>
          <ul className="mt-8 grid gap-6 md:grid-cols-3">
            {homeGuides.map((g) => (
              <li key={g.slug} className="border-t border-charcoal/10 pt-5">
                <Link
                  to="/blog/$slug"
                  params={{ slug: g.slug }}
                  className="font-serif text-xl leading-snug text-charcoal underline decoration-moss/40 underline-offset-4 transition-colors hover:decoration-moss"
                >
                  {g.title}
                </Link>
                <p className="mt-3 text-pretty text-sm leading-relaxed text-charcoal/70">{g.blurb}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Credentials / Accountability */}
        <Reveal>
          <section
            id="credentials"
            className="relative isolate overflow-hidden bg-charcoal py-16 text-cream sm:py-20 lg:py-24"
          >
            <div className="container-page relative">
              <div className="grid items-end gap-8 lg:grid-cols-2 lg:gap-16">
                <div>
                  <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.25em] text-sage sm:mb-5">
                    Accountability
                  </p>
                  <h2 className="max-w-[12ch] font-serif text-4xl leading-[1.06] sm:text-5xl lg:text-6xl">
                    Every collection, accounted for.
                  </h2>
                </div>
                <p className="max-w-[46ch] text-pretty text-base leading-relaxed text-cream/75 sm:text-lg">
                  From secure collection to documented destruction, every movement is recorded and every outcome
                  reported, giving your team a clear audit trail without added administration.
                </p>
              </div>

              <figure className="mt-10 overflow-hidden rounded-sm lg:mt-14">
                <img
                  src="/images/optimised/audit-scan-1024.webp"
                  srcSet="/images/optimised/audit-scan-640.webp 640w, /images/optimised/audit-scan-1024.webp 1024w, /images/optimised/audit-scan-1448.webp 1448w"
                  sizes="(min-width: 1280px) 1200px, 100vw"
                  alt="Operative scanning a sealed collection tag on a caged trolley of sorted uniforms"
                  width={1448}
                  height={1086}
                  loading="lazy"
                  decoding="async"
                  className="h-64 w-full object-cover object-[center_40%] sm:h-80 lg:h-[520px]"
                />
              </figure>

              <ol className="mt-10 grid gap-8 border-t border-cream/15 pt-8 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10 lg:mt-14 lg:grid-cols-4 lg:pt-9">
                {[
                  ["01", "Sealed collection", "Consoles and sacks sealed in transit."],
                  ["02", "Tracked transit", "Unmarked, GPS-tracked vehicles with CCTV."],
                  ["03", "Secure destruction", "Shredded beyond recognition at secure facilities."],
                  [
                    "04",
                    "Documented outcome",
                    "Electronic note of secure destruction plus weight-based recycling reporting.",
                  ],
                ].map(([n, title, body]) => (
                  <li key={n}>
                    <p className="font-serif text-4xl text-sage">{n}</p>
                    <h3 className="mt-3 font-serif text-xl text-cream">{title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-cream/70">{body}</p>
                  </li>
                ))}
              </ol>

              <a
                href="/brochure/preserve-earth-secure-disposal-brochure.pdf"
                className="mt-10 inline-flex w-full items-center justify-center gap-3 rounded-sm bg-sage px-6 py-3.5 text-sm font-medium text-charcoal transition-colors hover:bg-sage-light sm:w-auto lg:mt-12"
              >
                Download service brochure
                <DownloadIcon />
              </a>
            </div>
          </section>
        </Reveal>

        {/* Enquiry */}
        <Reveal>
          <section id="enquire" className="container-page pt-32 pb-20">
            <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
              <div>
                <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">Enquire</p>
                <h2 className="mb-8 font-serif text-4xl leading-tight">Discuss your requirements.</h2>
                <p className="mb-10 max-w-[42ch] text-charcoal/70">
                  For facilities, compliance and procurement teams, on-site assessments and tailored quotes are arranged
                  across the United Kingdom.
                </p>
                <dl className="space-y-4 text-sm">
                  <div className="flex items-center gap-4">
                    <dt className="w-16 text-charcoal/70">Email</dt>
                    <dd>
                      <a href="mailto:info@preserveearth.co.uk" className="hover:text-moss">
                        info@preserveearth.co.uk
                      </a>
                    </dd>
                  </div>
                  <div className="flex items-center gap-4">
                    <dt className="w-16 text-charcoal/70">Phone</dt>
                    <dd>
                      <a href="tel:+443300432265" className="hover:text-moss">
                        0330 043 2265
                      </a>
                    </dd>
                  </div>
                  <div className="flex items-center gap-4">
                    <dt className="w-16 text-charcoal/70">Social</dt>
                    <dd>
                      <a
                        href="https://www.instagram.com/preserveearthuk"
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-moss"
                      >
                        @preserveearthuk
                      </a>
                    </dd>
                  </div>
                </dl>
              </div>

              <EnquiryForm defaultInterest="Textile Recycling" />
            </div>
          </section>
        </Reveal>
      </main>

      <SiteFooter />
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      className="mt-0.5 size-4 shrink-0 text-moss"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg
      className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  );
}

const heroSteps = [
  { label: "Collection", icon: "truck" },
  { label: "Verified handling", icon: "shield" },
  { label: "Recovery", icon: "recycle" },
  { label: "Audit record", icon: "doc" },
] as const;

function StepIcon({ name }: { name: (typeof heroSteps)[number]["icon"] }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.4,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (name === "truck")
    return (
      <svg {...common} aria-hidden="true">
        <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
        <circle cx="7" cy="18" r="1.6" />
        <circle cx="17.5" cy="18" r="1.6" />
      </svg>
    );
  if (name === "shield")
    return (
      <svg {...common} aria-hidden="true">
        <path d="M12 3l7 3v5c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  if (name === "recycle")
    return (
      <svg {...common} aria-hidden="true">
        <path d="M7 8L9.5 4l2.5 4M4.8 15l-1-4.4 4.3 1.2M19.6 12.6l1.4 4.3-4.4.4" />
        <path d="M12 8l4 6.8M9.5 20h7.2M7 8L3.6 14" />
      </svg>
    );
  return (
    <svg {...common} aria-hidden="true">
      <path d="M14 3H7a1 1 0 00-1 1v16a1 1 0 001 1h10a1 1 0 001-1V7z" />
      <path d="M14 3v4h4M9 12h6M9 16h4" />
    </svg>
  );
}

function DesktopHeroContour() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setShow(desktop.matches && !reduce.matches);
    sync();
    desktop.addEventListener("change", sync);
    reduce.addEventListener("change", sync);
    return () => {
      desktop.removeEventListener("change", sync);
      reduce.removeEventListener("change", sync);
    };
  }, []);

  if (!show) return null;

  return (
    <Suspense fallback={null}>
      <HeroContour />
    </Suspense>
  );
}

function Hero() {
  return (
    <section className="relative isolate bg-charcoal text-cream">
      <DesktopHeroContour />
      <div className="relative z-[1] grid lg:grid-cols-2">
        {/* Copy panel */}
        <div className="order-1 flex items-center px-6 py-16 sm:px-10 lg:min-h-[820px] lg:py-16 lg:pl-[max(1.5rem,calc((100vw-80rem)/2+1.5rem))] lg:pr-16">
          <div className="w-full max-w-xl">
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-sage">Secure. Traceable. Circular.</p>
            <span className="mt-5 mb-8 block h-px w-16 bg-sage/60" />
            <h1 className="font-serif text-4xl leading-[1.02] text-cream sm:text-5xl xl:text-6xl">
              Secure textile recycling and confidential waste disposal for UK businesses.
            </h1>
            <p className="mt-7 max-w-[50ch] text-base leading-relaxed text-cream/75 sm:text-lg">
              Scheduled and one-off collections across the UK, GDPR-compliant shredding, IT &amp; media recycling and
              secure product destruction, with an electronic note of secure destruction on every job.
            </p>
            <p className="mt-4 font-serif text-lg italic text-sage">Every material accounted for.</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
              <a
                href="#enquire"
                className="group inline-flex items-center justify-center gap-2 rounded-sm bg-sage px-7 py-3.5 text-sm font-medium text-charcoal transition-all hover:-translate-y-0.5 hover:bg-sage-light"
              >
                Request a collection
                <ArrowRight />
              </a>
              <a
                href="#credentials"
                className="inline-flex items-center justify-center rounded-sm px-7 py-3.5 text-sm font-medium text-cream ring-1 ring-cream/25 transition-colors hover:bg-cream/10"
              >
                Explore our process
              </a>
            </div>

            {/* Process chain */}
            <ul className="mt-12 grid grid-cols-2 gap-x-6 gap-y-6 sm:flex sm:items-start sm:gap-0">
              {heroSteps.map((s, i) => (
                <li key={s.label} className="flex items-start sm:flex-1">
                  <div className="flex w-full flex-col items-start gap-3 text-left">
                    <span className="flex size-11 items-center justify-center rounded-full text-cream/85 ring-1 ring-cream/25">
                      <StepIcon name={s.icon} />
                    </span>
                    <span className="text-xs text-cream/70 sm:text-sm">{s.label}</span>
                  </div>
                  {i < heroSteps.length - 1 && (
                    <span className="mt-[22px] hidden h-px w-6 shrink-0 bg-cream/25 sm:block" />
                  )}
                </li>
              ))}
            </ul>

            <p className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-sage">
              <span>GDPR-compliant</span>
              <span className="text-cream/30">•</span>
              <span>Electronic note of secure destruction</span>
              <span className="text-cream/30">•</span>
              <span>UK-wide collections</span>
            </p>
            <p className="mt-4 text-xs text-cream/70">
              Preserve Earth is a trading name of RBM London Limited · Company No. 15482817
            </p>
          </div>
        </div>

        {/* Image panel */}
        <div className="relative order-2 h-[46vh] overflow-hidden lg:h-auto lg:min-h-[820px]">
          <img
            src="/images/hero/hero-textile-containers-1664.webp"
            srcSet={HERO_SRCSET}
            sizes="(min-width: 1024px) 50vw, 100vw"
            width={1664}
            height={936}
            alt="Aerial view of green steel containers filled with sorted textiles at a recycling facility"
            loading="eager"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/10" />
          {/* gradient fade from the copy panel: top on mobile, left on desktop */}
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-charcoal to-transparent lg:hidden" />
          <div className="absolute inset-y-0 left-0 hidden w-32 bg-gradient-to-r from-charcoal to-transparent lg:block" />
        </div>
      </div>
    </section>
  );
}

function Reveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`motion-reduce:opacity-100 motion-reduce:transform-none motion-reduce:transition-none transition-all duration-[900ms] ease-out-quart ${
        visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
      } ${className}`}
    >
      {children}
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );
}
